'use memo'

import {
  AutopilotTaskDto,
  useAssignTaskToUser,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxButton,
  AgxCollapsible,
  AgxEmptyState,
  AgxIconTask,
  AgxProgressBar,
  AgxSpacer,
  AgxTypography,
  IconTaskType,
  Icon_CheckmarkCircleOutline,
  Icon_Chevron,
  Icon_Clock,
} from '@urbanx/agx.ui-components2'
import TaskAssigneeSelect from 'components/tasks/TaskAssigneeSelect'
import AppConfig from 'config/AppConfig'
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates'
import {
  applyTaskUpdateToTasks,
  getStepCompletionPercentage,
  getTaskDomId,
} from 'utils/tasks'
import { splitPascalCase } from 'utils/stringUtils'
import { useGetAssigneeOptions } from '../TaskFilters/useGetAssigneeOptions'
import { TaskUpdatedNotification } from 'types/pushNotifications'
import 'components/tasks/Tasks.scss'

interface TaskModalEntryProps {
  title: string
  tasks: AutopilotTaskDto[]
  badge: ReactNode
  iconType: IconTaskType
  actionText: string
  onAction: (tasks: AutopilotTaskDto[]) => void
  notification?: ReactNode
  actionButtonId?: string
  panelKey?: string
  blockerContent?: ReactNode
}

const isTaskAssigned = (task?: AutopilotTaskDto) =>
  Boolean(task?.assignedTo && task.assignedTo !== 'Unassigned')

const TaskModalEntry = ({
  title,
  tasks,
  badge,
  iconType,
  actionText,
  onAction,
  notification,
  actionButtonId = 'taskModalEntry-action',
  panelKey = 'TaskModalEntryPanel',
  blockerContent,
}: TaskModalEntryProps) => {
  const { userAccount } = useContext(AuthContext)
  const currentUserId = AppConfig.userIdClaim ?? userAccount.agent
  const [displayTasks, setDisplayTasks] = useState(tasks)
  const allSteps = displayTasks.flatMap((task) => task.taskSteps ?? [])
  const campaignId = displayTasks[0]?.campaignId
  const contractId = displayTasks[0]?.contractId
  const allTasksCompleted = displayTasks.every(
    (task) => task.isCompleted === true
  )
  const unlockedTasks = displayTasks.filter((task) => task.isLocked !== true)
  const tasksToCheckAssignment =
    unlockedTasks.length > 0 ? unlockedTasks : displayTasks
  const allTasksAssigned = tasksToCheckAssignment.every(isTaskAssigned)
  const assignedTo = displayTasks[0]?.assignedTo
  const assigneeSelectId = getTaskDomId(
    `${panelKey}-assignToUserChip`,
    displayTasks[0]
  )
  const actionDisabled =
    !isTaskAssigned(displayTasks[0]) || assignedTo !== currentUserId

  useEffect(() => {
    setDisplayTasks(tasks)
  }, [tasks])

  const handleTaskUpdate = useCallback(
    (taskUpdate: TaskUpdatedNotification) => {
      setDisplayTasks((prevTasks) =>
        applyTaskUpdateToTasks(prevTasks, taskUpdate)
      )
    },
    []
  )

  useAutopilotTaskUpdates(handleTaskUpdate)

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { assigneeOptions } = useGetAssigneeOptions()
  const { assignTaskToUser } = useAssignTaskToUser({
    onSuccess: () => {
      if (campaignId) invalidateTasks(campaignId, contractId)
    },
    onError: () => setDisplayTasks(tasks),
  })

  const taskStepSummary = (
    <AgxSpacer fill gap="spacing-3xl">
      <div className="taskStep-container taskStep-summaryPanel">
        <AgxSpacer fill gap="spacing-lg">
          {allSteps.map((step) => {
            const name = splitPascalCase(step.name) ?? 'Untitled'
            const key = `${step.order ?? 'no-order'}-${name}`

            return (
              <AgxSpacer
                key={key}
                direction="horizontal"
                gap="spacing-md"
                contentAlignment="end"
                fill
                contentJustification="start"
              >
                {step.isCompleted ? (
                  <Icon_CheckmarkCircleOutline color="var(--fg-success)" />
                ) : (
                  <Icon_Clock color="var(--text-tertiary)" />
                )}
                <AgxTypography
                  id="steps"
                  size="small"
                  type="body_ui"
                  color={
                    step.isCompleted
                      ? 'var(--text-success)'
                      : 'var(--text-tertiary)'
                  }
                >
                  {name}
                </AgxTypography>
              </AgxSpacer>
            )
          })}
          {notification}
        </AgxSpacer>
      </div>
    </AgxSpacer>
  )

  return (
    <AgxCollapsible
      key={panelKey}
      open={!allTasksCompleted}
      enableToggle
      variant="withoutDivider"
      iconPosition="none"
      title=""
      contentPadding={{ left: 'xl', bottom: 'xl', right: 'xl' }}
      customTitle={
        <AgxSpacer direction="vertical" fill gap="spacing-ml">
          <AgxSpacer
            direction="horizontal"
            fill
            contentAlignment="center"
            contentJustification="space-between"
          >
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              contentAlignment="center"
            >
              <AgxIconTask
                type={iconType}
                iconColor="black"
                height={32}
                width={32}
                bgColor="var(--bg-tag-grey)"
              />
              <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                {title}
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer
              direction="horizontal"
              contentJustification="end"
              contentAlignment="center"
              gap="spacing-lg"
            >
              <div onClick={(event) => event.stopPropagation()}>
                <TaskAssigneeSelect
                  id={assigneeSelectId}
                  value={displayTasks[0]?.assignedTo}
                  options={!allTasksCompleted ? assigneeOptions : []}
                  onAssign={(userId) => {
                    if (!campaignId) return

                    setDisplayTasks((prevTasks) =>
                      prevTasks.map((task) => ({
                        ...task,
                        assignedTo: userId,
                      }))
                    )

                    displayTasks.forEach((task) => {
                      if (!task.id) return

                      assignTaskToUser({
                        taskId: task.id,
                        userId,
                      })
                    })
                  }}
                />
              </div>
              {badge}
            </AgxSpacer>
          </AgxSpacer>
          {allTasksCompleted ? (
            <AgxTypography
              id={`task-modal-entry-completed`}
              type={'caption'}
              size={'medium'}
              color="var(--text-success)"
            >
              Complete! 🎉
            </AgxTypography>
          ) : (
            <AgxProgressBar
              variant="slim"
              id="progress"
              percentageProgressPosition="left"
              mode="dark"
              progress={getStepCompletionPercentage(allSteps)}
            />
          )}
        </AgxSpacer>
      }
      content={
        blockerContent && !allTasksCompleted ? (
          <AgxSpacer fill className="taskModalEntry-blockerContent">
            {blockerContent}
          </AgxSpacer>
        ) : allTasksAssigned ? (
          <AgxSpacer fill gap="spacing-xl">
            {taskStepSummary}
            {!allTasksCompleted && (
              <AgxSpacer
                id="completeTaskRow"
                direction="horizontal"
                contentJustification="center"
                fill
                contentAlignment="center"
              >
                <AgxButton
                  id={actionButtonId}
                  variant="primary"
                  size="large"
                  text={actionText}
                  rightIcon={
                    <Icon_Chevron
                      width={18}
                      height={18}
                      direction={'right'}
                      color="var(--fg-brand-main)"
                    />
                  }
                  disabled={actionDisabled}
                  onClick={() => onAction(displayTasks)}
                />
              </AgxSpacer>
            )}
          </AgxSpacer>
        ) : (
          <AgxSpacer
            direction="horizontal"
            fill
            contentAlignment="center"
            contentJustification="center"
          >
            <AgxEmptyState
              variant="advanced"
              glyphType="locked"
              actionButtons={[]}
              label="Assign task to unlock"
            />
          </AgxSpacer>
        )
      }
    />
  )
}

export default TaskModalEntry
